import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import ConfirmDialog from '@components/Dialogs/ConfirmDialog';
import { CommonKeys } from '@components/EntityDrawers/constants/keys';
import useOwnerOptions from '@components/EntityDrawers/hooks/useOwnerOptions';
import RemovableListItem from '@components/Inputs/Autocomplete/items/RemovableListItem';
import ControlledAutocomplete, {
  ControlledAutocompleteProps,
} from '@components/Inputs/controllers/ControlledAutocomplete';
import { ISelectOption } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import useToggle from '@hooks/useToggle';

type Props = Pick<
  ControlledAutocompleteProps,
  'label' | 'placeholder' | 'readOnly' | 'required'
> & {
  name?: string;
};

const OwnersInput: FC<Props> = ({
  name = CommonKeys.Owners,
  required = true,
  readOnly,
  ...props
}) => {
  const { viewOnly } = useProject();
  const { ownersOptions } = useOwnerOptions(name);
  const [isRemoveOpened, removeControls, ownerOption] =
    useToggle<ISelectOption>();
  const { setValue, getValues } = useFormContext();

  const handleConfirmRemove = () => {
    if (!ownerOption?.value) {
      return;
    }

    const ownerValue = ownerOption.value.toString();

    // Remove owner from selected owners
    setValue(
      name,
      getValues(name).filter(
        (o: ISelectOption) => o.value !== ownerOption.value,
      ),
    );

    // Add owner to the list of owners to delete on save
    const ownersToDelete = getValues(CommonKeys.OwnersToDelete) || [];
    setValue(CommonKeys.OwnersToDelete, [...ownersToDelete, ownerValue], {
      shouldDirty: true,
    });

    removeControls.off();
  };

  return (
    <>
      <ConfirmDialog
        open={isRemoveOpened}
        onClose={removeControls.off}
        title="Delete owner"
        text={`Are you sure you want to remove ${
          ownerOption?.label ?? ''
        } from the list of owners? This action will affect all related entities and permanently delete the owner from the system.`}
        onConfirm={handleConfirmRemove}
      />

      <ControlledAutocomplete
        name={name}
        required={required}
        readOnly={viewOnly || readOnly}
        options={ownersOptions}
        sortValue
        itemComponent={RemovableListItem}
        itemProps={(option) => ({
          onRemove: () => removeControls.on(option),
        })}
        multiple
        freeSolo
        normalizeFreeSoloToOption
        canFlip
        {...props}
      />
    </>
  );
};

export default OwnersInput;
